import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { STORAGE_KEYS } from '@shared/constants/storage-keys';
import type { ThemeMode } from '@shared/theme/tokens';

interface ThemeState {
  /** User preference: 'light' | 'dark' | 'system'. */
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

const MEDIA = '(prefers-color-scheme: dark)';

/** Resolve a preference to the concrete mode that should be applied. */
export function resolveTheme(theme: ThemeMode): 'light' | 'dark' {
  if (theme === 'system') {
    return window.matchMedia(MEDIA).matches ? 'dark' : 'light';
  }
  return theme;
}

/** Apply the resolved theme to the document root. */
export function applyTheme(theme: ThemeMode): void {
  const resolved = resolveTheme(theme);
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      toggleTheme: () => {
        const next = resolveTheme(get().theme) === 'dark' ? 'light' : 'dark';
        applyTheme(next);
        set({ theme: next });
      },
    }),
    {
      name: STORAGE_KEYS.THEME,
      // Re-apply the persisted theme once state is rehydrated on load.
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme);
      },
    },
  ),
);
